import html2canvas from 'html2canvas';

/** Instagram Stories safe frame */
export const STORY_W = 1080;
export const STORY_H = 1920;
/** Margine intorno alla card (~10% per lato) */
export const STORY_PAD = 0.1;
export const JPEG_QUALITY = 0.92;

export const canvasToShareFile = (canvas, baseName) =>
  new Promise((resolve, reject) => {
    const toFile = (blob, ext, mime) => {
      if (!blob) return null;
      return new File([blob], `${baseName}.${ext}`, {
        type: blob.type || mime,
      });
    };

    canvas.toBlob(
      (jpegBlob) => {
        const jpegFile = toFile(jpegBlob, 'jpg', 'image/jpeg');
        if (jpegFile) {
          resolve(jpegFile);
          return;
        }
        canvas.toBlob((pngBlob) => {
          const pngFile = toFile(pngBlob, 'png', 'image/png');
          if (pngFile) resolve(pngFile);
          else reject(new Error('Blob empty'));
        }, 'image/png');
      },
      'image/jpeg',
      JPEG_QUALITY,
    );
  });

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = src;
  });

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('FileReader failed'));
    reader.readAsDataURL(blob);
  });

/** Converte un URL immagine in data URL (evita canvas tainted / CORS). */
const srcToDataUrl = async (src) => {
  if (!src || src.startsWith('data:')) return src;
  try {
    const res = await fetch(src, { mode: 'cors', credentials: 'omit', cache: 'force-cache' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return blobToDataUrl(await res.blob());
  } catch (_) {
    /* fallback: ridisegna su canvas con crossOrigin */
  }
  try {
    const img = await loadImage(src);
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext('2d');
    if (!ctx || !canvas.width || !canvas.height) throw new Error('canvas empty');
    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
  } catch (_) {
    return null;
  }
};

const waitForNodeImages = (node) => {
  const imgs = [...(node?.querySelectorAll?.('img') || [])];
  return Promise.all(
    imgs.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete && img.naturalWidth > 0) {
            resolve();
            return;
          }
          const done = () => resolve();
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
          window.setTimeout(done, 4000);
        }),
    ),
  );
};

/**
 * Compone un'immagine (già pronta o qualsiasi aspect) su canvas Stories 1080×1920.
 */
export const composeStoryShareCanvasFromImage = async (
  imageOrSrc,
  { background = '#000000', pad = STORY_PAD } = {},
) => {
  const img =
    typeof imageOrSrc === 'string' ? await loadImage(imageOrSrc) : imageOrSrc;

  const story = document.createElement('canvas');
  story.width = STORY_W;
  story.height = STORY_H;
  const ctx = story.getContext('2d');
  if (!ctx) throw new Error('2d context unavailable');

  ctx.fillStyle = background;
  ctx.fillRect(0, 0, STORY_W, STORY_H);

  const maxW = STORY_W * (1 - 2 * pad);
  const maxH = STORY_H * (1 - 2 * pad);
  const fit = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight, 1);
  const drawW = img.naturalWidth * fit;
  const drawH = img.naturalHeight * fit;
  const dx = (STORY_W - drawW) / 2;
  const dy = (STORY_H - drawH) / 2;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, dx, dy, drawW, drawH);

  return story;
};

/** Instagram Post (quadrato) */
export const POST_W = 1080;
export const POST_H = 1080;

/**
 * Cattura un nodo DOM a scala alta e lo compone su canvas (default Stories 1080×1920,
 * `outWidth`/`outHeight` per altri formati, es. post 1080×1080).
 * Clona off-screen + inline immagini in data-URL per non condividere solo l’avatar (CORS/taint).
 */
export const composeStoryShareCanvasFromNode = async (
  captureNode,
  { outWidth = STORY_W, outHeight = STORY_H, pad = STORY_PAD } = {},
) => {
  if (!captureNode) throw new Error('Capture node missing');

  try {
    await document.fonts?.ready;
  } catch (_) {
    /* ignore */
  }
  await waitForNodeImages(captureNode);

  const rect = captureNode.getBoundingClientRect();
  const width = Math.max(1, Math.ceil(captureNode.offsetWidth || rect.width));
  const height = Math.max(1, Math.ceil(captureNode.offsetHeight || rect.height));

  const host = document.createElement('div');
  host.setAttribute('data-story-capture-host', 'true');
  host.style.cssText = [
    'position:fixed',
    'left:-10000px',
    'top:0',
    `width:${width}px`,
    `height:${height}px`,
    'opacity:1',
    'pointer-events:none',
    'z-index:-1',
    'overflow:hidden',
  ].join(';');

  const clone = captureNode.cloneNode(true);
  clone.style.width = `${width}px`;
  clone.style.height = `${height}px`;
  clone.style.maxWidth = `${width}px`;
  clone.style.margin = '0';
  clone.style.transform = 'none';
  host.appendChild(clone);
  document.body.appendChild(host);

  try {
    const imgs = [...clone.querySelectorAll('img')];
    await Promise.all(
      imgs.map(async (img) => {
        const src = img.currentSrc || img.getAttribute('src') || '';
        const dataUrl = await srcToDataUrl(src);
        if (dataUrl) {
          img.removeAttribute('srcset');
          img.crossOrigin = 'anonymous';
          img.src = dataUrl;
        } else {
          img.removeAttribute('src');
          img.style.visibility = 'hidden';
        }
      }),
    );
    await waitForNodeImages(clone);

    const dpr = window.devicePixelRatio || 1;
    const scale = Math.max(2, Math.min(3, Math.ceil(dpr * 2)));

    const cardCanvas = await html2canvas(clone, {
      allowTaint: false,
      useCORS: true,
      backgroundColor: '#000000',
      scale,
      width,
      height,
      windowWidth: width,
      windowHeight: height,
      logging: false,
      imageTimeout: 15000,
      foreignObjectRendering: false,
    });

    if (!cardCanvas.width || !cardCanvas.height) {
      throw new Error('html2canvas empty');
    }

    const story = document.createElement('canvas');
    story.width = outWidth;
    story.height = outHeight;
    const ctx = story.getContext('2d');
    if (!ctx) throw new Error('2d context unavailable');

    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, outWidth, outHeight);

    const maxW = outWidth * (1 - 2 * pad);
    const maxH = outHeight * (1 - 2 * pad);
    const fit = Math.min(maxW / cardCanvas.width, maxH / cardCanvas.height);
    const drawW = cardCanvas.width * fit;
    const drawH = cardCanvas.height * fit;
    const dx = (outWidth - drawW) / 2;
    const dy = (outHeight - drawH) / 2;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const radius = Math.min(drawW, drawH) * 0.04;
    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 56;
    ctx.shadowOffsetY = 20;
    ctx.fillStyle = '#000000';
    if (typeof ctx.roundRect === 'function') {
      ctx.beginPath();
      ctx.roundRect(dx, dy, drawW, drawH, radius);
      ctx.fill();
    } else {
      ctx.fillRect(dx, dy, drawW, drawH);
    }
    ctx.restore();

    ctx.drawImage(cardCanvas, dx, dy, drawW, drawH);
    return story;
  } finally {
    host.remove();
  }
};

export const downloadShareFile = (file) => {
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/** Safari: apri l’immagine per “Aggiungi a Foto” */
export const openImageForPhotosSave = (file) => {
  const url = URL.createObjectURL(file);
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (!opened) {
    downloadShareFile(file);
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return false;
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return true;
};

/** Chrome su iOS (WKWebView): utile per messaggi UX; lo share sheet resta navigator.share. */
export const isChromeIOS = () =>
  /CriOS/i.test(navigator.userAgent || '') &&
  /iPhone|iPad|iPod/i.test(navigator.userAgent || '');

const fileToPngBlob = async (file) => {
  if (file?.type === 'image/png') return file;
  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close?.();
    return await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  } catch (_) {
    return null;
  }
};

/** Clipboard PNG + deep link Instagram Stories (fallback iOS, non Chrome). */
export const tryInstagramStories = async (file) => {
  if (!/iPhone|iPad|iPod/i.test(navigator.userAgent || '')) return false;
  // Su Chrome iOS il deep link apre IG senza immagine → evita
  if (isChromeIOS()) return false;
  if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) return false;

  try {
    const blob = await fileToPngBlob(file);
    if (!blob) return false;

    await navigator.clipboard.write([
      new ClipboardItem({ 'image/png': Promise.resolve(blob) }),
    ]);
    window.location.href = 'instagram-stories://share?source_application=zetabarber';
    return true;
  } catch (err) {
    console.warn('Instagram Stories deep link failed', err);
    return false;
  }
};

/**
 * Flusso share unificato: share sheet iOS/Android → fallback Stories → Foto → download.
 * Se Web Share c’è ma fallisce (gesto scaduto tipico al 1° tap), ritorna 'retry'
 * invece di aprire l’immagine in una nuova scheda.
 * @returns {'shared'|'aborted'|'retry'|'saved'|'downloaded'|'error'}
 */
export const runStoryShareFlow = async ({
  file,
  isMobile = false,
  onToast,
}) => {
  const ua = navigator.userAgent || '';
  const isTouchDevice = isMobile || /iPhone|iPad|iPod|Android/i.test(ua);
  const canShareApi = typeof navigator.share === 'function';

  const tryShareFiles = async (shareFile = file) => {
    if (!canShareApi) return false;
    try {
      if (
        typeof navigator.canShare === 'function' &&
        !navigator.canShare({ files: [shareFile] })
      ) {
        return false;
      }
      await navigator.share({ files: [shareFile] });
      return true;
    } catch (err) {
      if (err?.name === 'AbortError') return 'aborted';
      // Gesto utente scaduto dopo html2canvas (Chrome/Safari iOS)
      if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') {
        return 'gesture';
      }
      console.warn('Story share failed', err);
      return false;
    }
  };

  if (isTouchDevice) {
    if (canShareApi) {
      const result = await tryShareFiles();
      if (result === true) return 'shared';
      if (result === 'aborted') return 'aborted';
      // File pronto in cache: al prossimo tap lo sheet si apre subito
      onToast?.('retry');
      return 'retry';
    }

    if (await tryInstagramStories(file)) return 'shared';

    const opened = openImageForPhotosSave(file);
    onToast?.(opened ? 'photos' : 'fallback');
    return 'saved';
  }

  const canShareFiles =
    typeof navigator.canShare === 'function' ? navigator.canShare({ files: [file] }) : false;
  if (canShareApi && canShareFiles) {
    const result = await tryShareFiles();
    if (result === true) return 'shared';
    if (result === 'aborted') return 'aborted';
  }

  downloadShareFile(file);
  return 'downloaded';
};
